import { Color, ColorConfig } from 'app/shared/collections/StatDefinition';
import { DEFAULT_CONFIG } from './default_config.value';
import { TimeIntervalChunkerConfig } from 'app/shared/collections/time/queue/time_interval_chunker.factory';
import { ListItem } from 'app/overview/triggers/builder_interfaces';
import { HeatMapInclusionArray } from 'app/map/services/heatmap/HeatmapDataRequesterFactory';
import { DynamicSchemaTypeDict } from 'app/config/user_generated_config.service';
import { HeatMapSelectableOptions } from 'app/overview/config/configuration_tab/general/frontend_config/heatmapSelector/heatmap_selector';
import { KmzWmsLayerUrl } from 'app/shared/kmz_maker/kml_maker.service';
import { InfraType } from 'app/agriculture/mutations/types/entityConfig';

// our config overrides
const appConfig = (window as any).APP_CONFIG || {};

// we merge our alerting config in a very custom way;
//  if our overriding config is a boolean, keep it so,
//  otherwise merge together our overrides with our defaults.
const alertingConfig: AlertingConfig =
    typeof appConfig.alerting === 'boolean'
        ? appConfig.alerting
        : Object.assign({}, DEFAULT_CONFIG.alerting, appConfig.alerting);

type AlertingConfig =
    | {
          email: boolean;
          sms: boolean;
          ticket: boolean;
          auto_acknowledge: boolean;
      }
    | boolean;

export interface PacketConfig {
    humanName: string;
    logicalName:
        | 'packets1'
        | 'packets2'
        | 'packets3'
        | 'packets4'
        | 'packets5'
        | 'packets6'
        | 'packets7'
        | 'packets8';
    colorRendererConfig?: ColorConfig;
}

interface TriggerOption {
    groupByListItem: Array<ListItem>;
    fields: any;
    groupByDisplayNames: any;
}

/**
 * For example, when someone makes a dashboard item
 *  using one of the given data sources, add this particular grouping/filter option
 *
 * Example:
 {
 "type": "dashboard_relationship_option",
 "display_name": "Connected Venus",
 "description": "The connected Victron Venus device",
 "available_schemas": ['victron_battery_stats'],
 "relationship_table": "victron_relationship_stat",
 "relationship_field": "contained_in_asset_id",
 "target_asset_type_name": "Victron Venus",
 "filter_by_asset_type_names": ['Victron Venus']
 "selection_placeholder": "Select a Venus"
 }
 */
export interface DashboardRelationshipOption {
    /**
     * the name of the option, for tracking uniqueness, stored in the dashboard item config itself
     */
    name: string;
    /**
     * Using facing name for the option, eg: Victron Venus
     * Group By: Victron Venus Avg
     * Filter By: Victron Venus
     */
    display_name: string;
    /**
     * User facing description, potentially shown as tooltips, etc.
     */
    description: string;
    /**
     * A list of dynamic stat schema names for which this option is available for
     */
    available_schemas: Array<string>;
    /**
     * The name of the relationship schema to follow
     */
    relationship_table: string;

    /**
     * The column of the relationship schema representing the foreign key
     */
    relationship_field: string;

    /**
     * The name of the asset type representing the grouped or filtered asset through the relationship
     *  which is searched for
     */
    target_asset_type_name: string;
    /**
     * When applying the option as a filter,
     *  filter the selectable assets, by the following asset types, using
     *  fully matched asset type names
     */
    filter_by_asset_type_names: Array<string>;

    /**
     * Optionally describe how to prompt the user when selecting an item
     * eg: "Select a Trailer"
     */
    selection_placeholder: string | null;
}

type StatBrowserTextDropdownCell = {
    type: 'text_dropdown';
    /**
     * If the user can provide a custom string value if there's no matching dropdown option
     */
    canSetCustomValue: boolean;
    /**
     * List of options to show in the dropdown
     */
    options: Array<{ label: string; value: string }>;
};

export type StatBrowserCustomCell = StatBrowserTextDropdownCell;
export interface ImsHealthConfig {
    url: string;
    refreshInterval: number;
    ageTolerance: number;
    hideIfUnreliable: boolean;
    hideOldContainers: boolean;
}

interface AgMapSnappingConfig {
    snappingSearchRadius: number;
    runShapePickingFrameGap: number;
}

export const ConfigService: ConfigService = {
    ...DEFAULT_CONFIG,
    ...appConfig,
    ...{ alerting: alertingConfig },
};

export type ConfigService = {
    /**
     * @desc Provides entities support for the triggers dropdowns
     */
    triggerOptions: Array<TriggerOption>;
    dashboardRelationshipOptions: Array<DashboardRelationshipOption>;
    imsName: string;
    /**
     * @desc The time span of data to be loaded in minutes.
     * @default 30
     */
    timelineMinutes: number;
    /**
     * @desc The API Key used to access Cesium Ion Map data.
     */
    cesiumIonApiKey: string;
    /**
     * @desc terrain providers for the map
     */
    terrainProviders: Array<any>;
    /**
     * @desc The imagery layer form will show a warning if the detail level is above this value.
     * @default 15
     */
    terrainDetailHighWarningLevel: number;
    /**
     * @desc The imagery layer form will show a warning if the detail level is below this value.
     * @default 12
     */
    terrainDetailLowWarningLevel: number;
    /**
     * @desc The imagery layers for the map
     */
    imageryLayers: Array<any>;
    apiUrlRoot: string;
    cameraUrlRoot: string;
    /**
     * @desc Cameras (cctv) username
     * @default "user"
     */
    camerasUsername: string;
    /**
     * @desc Cameras (cctv) password
     * @default "pass"
     */
    camerasPassword: string;
    /**
     * @desc To enable alerts set this to true, and optionally set email and sms as well
     * @default {
     *         email: false,
     *         sms: false,
     *         ticket: true,
     *         auto_acknowledge: true
     *     }
     */
    alerting: AlertingConfig;
    /**
     * @desc To enable zones set this to true
     * @default true
     */
    zones: boolean;
    /**
     * @desc Timeline playback loops when this is enabled
     * @default false
     */
    loopTimeline: boolean;
    /**
     * @desc Demo mode enables a few demo-only features such as all right click options
     * @default false
     */
    demoMode: boolean;
    /**
     * @desc An array of tags (e.g. ['ImsPanelService']) that are used to enable logging
     * @default []
     */
    debug: string[];
    /**
     * @desc Some services use this flag to change behaviour in order to make dev
     * @default false
     */
    devMode: boolean;
    /**
     * @desc RF Frequency used for coverage planning in Mhz
     * @default 2337
     */
    baseRFFrequency: number;
    /**
     * @desc RF Broadcast power used for coverage planning in milliwatts
     * @default 100
     */
    baseRFBroadcastPower: number;
    /**
     * @desc RF Tx antenna gain used for coverage planning in decibels
     * @default 0
     */
    baseRFTxAntennaGain: number;
    /**
     * @desc RF Rx antenna gain used for coverage planning in decibels
     * @default 0
     */
    baseRFRxAntennaGain: number;
    /**
     * @desc RF Tx antenna height used for coverage planning in metres
     * @default null
     */
    baseRFTxAntennaHeight: number | null;
    /**
     * @desc RF Rx antenna height used for coverage planning in metres
     * @default 4
     */
    baseRFRxAntennaHeight: number;
    /**
     * @desc RF minimum received signal strength used for coverage planning in decibels
     * @default -80
     */
    baseRFRSSICutoff: number;
    /**
     * @desc logical viewControllers tab - this currently only contains the next hop graph
     * @default true
     */
    logicalViews: boolean;
    /**
     * @desc Show the 'Cameras' tab in the overview pane
     * @default false
     */
    camerasTab: boolean;
    /**
     * @desc Show the incidents / tickets tab
     * @default true
     */
    ticketsTab: boolean;
    /**
     * @desc The IP Device tags that are displayed in the center and in the available functions to select for IP Devices
     * @default ['Core', 'Ingress', 'Backhaul', 'Trailer', 'Vehicle', 'AHS']
     */
    aggregateTableTags: Array<string>;
    /**
     * @desc Enabling OIDC will show the login button in the login page and allow the OIDC auth flow
     * @default false
     */
    oidcEnabled: boolean;
    /**
     * @desc Enabling will only allow the OIDC auth flow, redirecting users to the oidc login
     * @default false
     */
    oidcOnlyAuthEnabled: boolean;
    /**
     * @desc Customize the OIDC login button text
     * @default null
     */
    oidcLoginLabel: string | null;
    /**
     * @desc
     *     Set allowUnsafeCredentialsInclude if you need to cookies to be sent with requests within different origins.
     *
     *     One example is where cypress e2e uses a custom port on the front end and the api, which requires this for OIDC to work.
     * @default null
     */
    allowUnsafeCredentialsInclude: boolean | null;
    /**
     * @desc Ability to change password
     * @default false
     */
    allowChangePassword: boolean;
    /**
     * @desc KMZ Functionality turned on
     * @default true
     */
    generateKmz: boolean;
    /**
     * Allow entities to be displayed when zoomed out
     * @default false
     */
    globalFarmMode: boolean;
    baseGlobeColor: Color;
    solarControllerBatteryVoltageColorRendererConfig: ColorConfig;
    solarControllerPanelVoltageColorRendererConfig: ColorConfig;
    solarControllerBatteryTempColorRendererConfig: ColorConfig;
    solarControllerBatteryCurrentColorRendererConfig: ColorConfig;
    solarControllerPanelCurrentColorRendererConfig: ColorConfig;
    solarControllerInputPowerColorRendererConfig: ColorConfig;
    solarControllerOutputPowerColorRendererConfig: ColorConfig;
    rssiColorRendererConfig: ColorConfig;
    rsrpColorRendererConfig: ColorConfig;
    rsrqColorRendererConfig: ColorConfig;
    snrColorRendererConfig: ColorConfig;
    noiseColorRendererConfig: ColorConfig;
    channelColorRendererConfig: ColorConfig;
    sinrColorRendererConfig: ColorConfig;
    costColorRendererConfig: ColorConfig;
    channelUtilisationColorRendererConfig: ColorConfig;
    hardwareStatColorRendererConfig: ColorConfig;
    latencyColorRendererConfig: ColorConfig;
    connectivityColorRendererConfig: ColorConfig;
    txRateColorRendererConfig: ColorConfig;
    rxRateColorRendererConfig: ColorConfig;
    packetConfigs: Array<PacketConfig>;
    /**
     * @desc Removes the base map layer, this is required if there is no internet access to the front end
     * @default false
     */
    removeBaseBingLayer: boolean;
    /**
     * @desc
     *     Attempts to retrieve the IP Address of the front-end docker container's host and set it as apiUrlRoot.
     *     It will use the existing port number
     * @default false
     */
    autoCalcApiIP: boolean;
    /**
     * @desc Whether the 3D Terrain toggle is enabled for the map (experimental)
     * @default true
     */
    map3DTerrainEnabled: boolean;
    /**
     * @desc The seconds difference between the real current time and the app's current time
     * @default 30
     */
    liveLatency: number;
    /**
     * @desc
     *     The minimum distance (in seconds) between the edge of the timeline and the cursor.
     *
     *     How close the cursor can be before the timeline is pushed forwards or backwards.
     *
     *     In live mode, this is the distance between the cursor and app current time.
     * @default 20
     */
    streamMargin: number;
    /**
     * @desc
     *     The distance (in seconds) on either side of the app current time that is still considered 'live mode'.
     *     If the cursor is withing this number of seconds of the app current time it will stick to live mode.
     * @default 20
     */
    liveTolerance: number;
    /**
     * @desc
     *     The scalar value of a dashboard items timeFrame which dictates how fast it should refresh.
     *
     *     E.g. 1/15 of a chart time frame of 30 minutes would mean the chart would refresh every 2 minutes the cursor moves.
     * @default 1 / 15
     */
    dashboardRefreshMultiplier: number;
    /**
     * @desc The throttle time (in milliseconds) for time collections listening for time change events
     * @default 2000
     */
    statCollectionRefreshThrottle: number;
    /**
     * @desc Require a google account to login
     * @default false
     */
    requireGoogleSignIn: boolean;
    /**
     * @desc The client id to use for oauth sign in (console.cloud.google.com)
     */
    googleClientID: string;
    /**
     * @desc Prototype view label override
     * @default "Prototype"
     */
    prototypeViewLabel: string;
    /**
     * @desc
     *     The default view that will be used on page load.
     *
     *     Note if you want the prototype view, but it has been labeled something else with the prototypeViewLabel,
     *     you still should use the view name 'prototype'
     * @default null
     */
    defaultViewName: string | null;
    enableIMSQL: string | null;
    /**
     * @desc Max panels per dashboard tab to permit
     * @default 25
     */
    maxPanelsInDashboardTab: number;
    /**
     * @desc Note, this won't actually be respected until some work has been done in the API and frontend
     * @default 100000000
     */
    maxUploadSize: number;
    /**
     * @desc Max number of dashboard tabs to permit
     * @default 20
     */
    maxTabsInDashboard: number;
    /**
     * @desc Whether QR Code is shown in the frontend
     * @default false
     */
    qrCodeEnabledForDesktop: boolean;
    /**
     * @desc Partially completed feature. Only turn on for dev experimentation.
     * @default false
     */
    enableStoreAndForward: boolean;
    /**
     * @desc
     *     Enable the overview config tab (boolean).
     *     Will only be visible if the user is staff, admin or a member of the 'ims_manager' group
     * @default true
     */
    enableConfigTab: boolean;
    /**
     * @desc The amount of time the frontend will wait for rf plan terrain height sampling to complete
     * @default 20000
     */
    rfPlanTimeoutMs: number;
    /**
     * @desc
     *     The amount of time MapService.getHeight will wait for cesium to sample the
     *     height of a single point. Currently only effects the context menu.
     * @default 1500
     */
    sampleHeightTimeoutMs: number;
    /**
     * @desc The upper limit when fetching heat map data points
     * @default 75000
     */
    heatMapDataLimit: number;
    /**
     * @desc The interpolation seconds limit to use when showing 'not interpolated' data
     * @default 10
     */
    heatMapInterpolationLimitSeconds: number;
    /**
     * @desc The number of times to try per each heatmap chunk
     * @default 2
     */
    heatMapDatabaseFailLimitPerChunk: number;
    /**
     * @desc the number of times a heatmap chunk database call can fail (per heatmap)
     * @default 10
     */
    heatMapDatabaseFailLimit: number;
    /**
     * @desc
     *     The maximum number of seconds that locations can be interpolated over.
     *
     *     If the time between two location samples is greater than this value, then the location
     *     will hold at the previous sample and not be interpolated.
     * @default 600
     */
    locationInterpolationCutoffSeconds: number;
    /**
     * @desc
     *     When true, will pull the front end config stored in the API and overlay it over the contents of
     *     config.js. When false, only config.js will be used. This should only be set to false for dev!
     * @default true
     */
    enableApiStoredFrontEndConfig: boolean;
    /**
     * @desc whether to request all custom forms be opened in debug mode
     * @default false
     */
    openFormsInDebugMode: boolean;
    /**
     * @desc this determines the size of each heatmap request.
     * @default 25000
     */
    heatMapChunkSize: number;
    heatMapSelectableOptions: HeatMapSelectableOptions;
    pixelsPerKmSelectableOptions: any;
    pixelsPerKmDefaultOption: number;
    /**
     * @desc maximum resolution for rf planing
     * @default 1600
     */
    rfPlanMaxResolution: number;
    /**
     * @desc
     *     This is the 'granularity' of IMS.
     *     This is the max frequency (in seconds) that the infobox data and map colors will update
     * @default 1
     */
    imsBaseTimeResolution: number;
    /**
     * @desc The default Cesium framerate
     * @default 60
     */
    framerate: number;
    /**
     * @desc Base timeout in milliseconds for GET requests
     * @default 120000
     */
    maxGetTimeout: number;
    /**
     * @desc Base timeout in milliseconds for POST, PUT, PATCH and DELETE requests
     * @default 120000
     */
    maxPostPutPatchDeleteTimeout: number;
    /**
     * @desc ims quickstart guide link
     * @default 'https://ftpsolutions.helpjuice.com/en_AU/ims-quick-start-guide/'
     */
    imsQuickStartGuideLink: string;
    /**
     * @desc add a black outline to connection lines
     * @default false
     */
    connectionOutline: boolean;
    /**
     * @desc display throughput (presentation of avg_rx_bps and avg_tx_bps) as bits if true (bytes if false)
     * @default true
     */
    throughputAsBits: boolean;
    /**
     * @desc a http service currently living in the back-end container which gives us access to the remote instance service
     */
    genericInterrogatorServiceUrl: string;
    bottomChartRadioStatOrderingWhitelist: Array<any>;
    /**
     * @desc Display an FPS counter on the Cesium map. Used for staged environments
     * @default false
     */
    debugShowFramesPerSecond: boolean;
    dynamicSchemaTypeDict: DynamicSchemaTypeDict;
    /**
     * @desc
     *     Similar to the dynamic stat data frequency value
     *     IMPORTANT: given the way this variable is currently used in the frontend code, it's not possible to allow
     *     this to be set in the API's front_end_config end point.
     * @default 8
     */
    ipDeviceStatDataFrequency: number;
    /**
     * @default 36
     */
    cesiumAssetPixelSize: number;
    /**
     * @default 14
     */
    cesiumAssetLabelPixelSize: number;
    /**
     * @default 13
     */
    cesiumIndicatorLabelPixelSize: number;
    /**
     * @default 15
     */
    cesiumZoneLabelPixelSize: number;
    /**
     * @desc This is the mac address prefix for IoTBoxes.
     * @default "5a:e7:ec"
     */
    macPrefix: string;
    macParser: RegExp | ((qrcode: string) => string);
    macParserGroup: number;
    timeSyncThreshold: number;
    timeSyncCheckPeriod: number;
    timeSyncHttpRequestTimeout: number;
    maxNumberOfSlices: number;
    showMacAddressQRCodeButton: boolean;
    rssiMapColorRendererConfig: ColorConfig | null;
    solarControllerBatteryVoltageColorConfig: ColorConfig | null;
    timeIntervalChunkSolarControllerStats: TimeIntervalChunkerConfig;
    timeIntervalChunkIpDeviceLog: TimeIntervalChunkerConfig;
    timeIntervalChunkTriggerStats: TimeIntervalChunkerConfig;
    timeIntervalChunkLocationStats: TimeIntervalChunkerConfig;
    timeIntervalChunkDynamicStats: TimeIntervalChunkerConfig;
    timeIntervalChunkIpDeviceStats: TimeIntervalChunkerConfig;
    showTimeSliceOptionSingleSlice: boolean;
    /**
     * @desc feature flag for the new date and time widgets
     * @default false
     */
    useNewDateTimeWidgets: boolean;
    /**
     * @desc
     *     should the cesium asset manager update the view components per frame (with location stats),
     *     or throttled to the base time resolution thing (default)
     * @default false
     */
    updateAssetViewComponentsWithLocation: boolean;
    /**
     * @desc
     *     This is the minimum panel width for dashboard items.
     *
     *     The dashboard is 12 columns wide, so a width of 4 means no more than 3 panels per row.
     *
     *     Don't set this to greater than 4. it will break default tabs!
     * @default 4
     */
    gridsterMinSizeX: number;
    /**
     * @desc Automatically reduce the header font size for dashboard panels when they are too small.
     * @default true
     */
    autoResizeDashboardPanelHeaderText: boolean;
    /**
     * @desc feature flag for the new feature layer
     * @default true
     */
    enableFeatureLayers: boolean;
    /**
     * @desc feature flag for the base globe colour in the admin config
     * @default true
     */
    enableBaseGlobeColour: boolean;
    /**
     * @desc feature flag for exposing the IPDevice.sibling_asset_id field
     * @default false
     */
    enableSiblingAssetForIPDevices: boolean;
    /**
     * @desc feature flag for exposing the Asset.is_legacy_anchor field
     * @default false
     */
    enableIsLegacyAnchorForAssets: boolean;
    /**
     * @default 0
     */
    defaultFarmId: number;
    /**
     * @desc
     *     for Cypress testing only!
     *
     *     this swallows the errors that can happen somewhere between the
     *     fast interactions that it does and the {force: true} behaviour- doesn't seem to come up in prod
     *     so you shouldn't need this in prod (and shouldn't use it!)
     * @default false
     */
    convertDestructorDestroyErrorsToWarnings: boolean;
    /**
     * @desc centralize cursor in timeline if a timestamp is selected outside the current timeframe
     * @default true
     */
    centralizeTimelineCursor: boolean;
    /**
     * @desc
     *     only effects the licensing page
     *
     *     users that are not on the model will not see the licensed vs active count module
     * @default false
     */
    clientOnNewPricingModel: boolean;
    /**
     * @desc feature flag for the search widget
     * @default true
     */
    enableLocationSearch: boolean;
    /**
     * @desc text to display as the search widget's placeholder
     * @default 'Enter an Asset, IP Device, Zone, Address or GPS coordinates'
     */
    locationSearchInputText: string;
    /**
     * @default 150
     */
    thumbnailHeight: number;
    /**
     * @default 150
     */
    thumbnailWidth: number;
    statBrowserCustomCells: Record<string, Record<string, StatBrowserCustomCell>>;
    /**
     * @desc Populate the dropdown for dynamic stat STRING filter fields in Triggers and HeatMap with distinct subgroup values from the database
     * @default true
     */
    enableDropdownForDynamicStringFields: boolean;
    imsHealthConfig: ImsHealthConfig;
    /**
     * @desc whether to get and display the /service_heath/ endpoint data
     * @default true
     */
    enableImsHealth: boolean;
    /**
     * @desc feature flag for enabling the Asset Integration Template functionality (toggled off - new feature under development)
     * @default false
     */
    enableAssetIntegrationTemplate: boolean;
    /**
     * @desc feature flag for enabling adding the ip device name to the radio tooltip on dashboards
     * @default true
     */
    addIPDeviceNameToRadioChartTooltip: boolean;
    /**
     * @default false
     */
    enableArcGisTerrain: boolean;
    /**
     * @desc
     *     An inclusion list of the type/column combos that will use the
     *     time chunked heat map as opposed to the limit chunked heat map.
     *
     *     In general this method is less preferred because it will be a great deal slower!
     */
    timeChunkedHeatMapDataRequesterInclusionlist: HeatMapInclusionArray;
    /**
     * @desc The initial size of the heat map chunk when using time based chunking
     * @default 600
     */
    initialHeatMapTimeChunkSizeSeconds: number;
    /**
     * @desc The amount to adjust the heatmap chunk size when using time based chunking
     * @default 600
     */
    heatMapTimeChunkSizeSecondsAdjustment: number;
    /**
     * @desc feature flag for enabling the detailed next hop information in the infobox
     * @default false
     */
    showDetailedNextHopInfo: boolean;
    /**
     * @desc feature flag for enabling the ServiceNow integration (toggled off - new feature under development)
     * @default false
     */
    enableServiceNowIntegration: boolean;
    /**
     * @desc whether to use the default device pixel ratio or 1.0
     * @default false
     */
    useDevicePixelRatio: boolean;
    /**
     * @desc timeout for the camera preview page in milliseconds
     * @default 15000
     */
    cameraPreviewPageLoadTimeout: number;
    /**
     * @desc set up cesium to allow for terrain overlays
     * @default false
     */
    enableTerrainOverlays: boolean;
    /**
     * @desc Used for agriculture: Reduces the opacity of all unselected assets when an asset is selected
     * @default false
     */
    enableOpacityChangeOnAssetSelection: boolean;
    agMapSnappingConfig: AgMapSnappingConfig;
    enableChartFilterByWifiOptions: boolean;
    /**
     * @desc whether to enable the path profiling between two assets via the context menu
     * @default true
     */
    showAssetPathProfileInContextMenu: boolean;
    extraTriggerTimeFrames: Record<string, { display: string }>;
    centerPanelLinkButtons: Array<{
        displayName: string;
        url: string;
    }>;
    /**
     * @desc show the contact FTP button in the top-bar help menu
     * @default true
     */
    showContactFTPButton: boolean;
    /**
     * @desc show the ims survey button in the top-bar help menu
     * @default true
     */
    showIMSSurveyButton: boolean;
    /**
     * @desc show the support ticket button in the top-bar help menu
     * @default true
     */
    showSupportTicketButton: boolean;
    /**
     * @desc allow adding pollers to IMS from the front-end
     * @default false
     */
    enablePollerAdd: boolean;
    /**
     * @desc Enables a way to get the asset menu to appear in ag mode
     * @default false
     */
    enableAgricultureAssetsMenu: boolean;

    bingAPIKey: string;
    kmzWmsLayerUrl: KmzWmsLayerUrl | null;

    sentryDsn: string | null;
    sentryRelease: string | null;

    /**
     * @desc default email for help desk links
     */
    defaultHelpEmail: string;

    /**
     * @desc Enables a delay on all axios responses
     * @default false
     */
    simulateResponseDelayMilliseconds?: number;
};
